import { useState } from 'react';
import PropTypes from 'prop-types';
import { Avatar } from '@components/ui';
import { NotificationPanel } from '@components/features';
import { useAuthStore, useUIStore, useThemeStore } from '@store';
import { useUnreadCount, useProfile } from '@hooks';

export default function Header({ projects = [], selectedProject, onProjectChange, onNewTask, onNewProject, onEditProject }) {
  const { user, logout } = useAuthStore();
  const { toggleSidebar, setActiveView } = useUIStore();
  const { theme, toggleTheme } = useThemeStore();
  const [showNotifications, setShowNotifications] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);

  // Obtener datos actualizados del usuario desde la BD
  useProfile();

  const isDark = theme === 'dark';

  const { data: unreadData } = useUnreadCount();
  const unreadCount = unreadData?.count || 0;

  const userInitials = user?.name
    ?.split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase() || 'U';

  const headerStyle = {
    backgroundColor: isDark ? '#1e293b' : 'white',
    padding: '16px 24px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    boxShadow: isDark ? '0 1px 3px rgba(0,0,0,0.2)' : '0 1px 3px rgba(0,0,0,0.05)',
    transition: 'background-color 0.3s ease',
  };

  const buttonStyle = {
    padding: '10px',
    backgroundColor: isDark ? '#334155' : '#f1f5f9',
    border: 'none',
    borderRadius: '10px',
    fontSize: '16px',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    color: isDark ? '#e2e8f0' : '#1e293b',
  };

  const selectStyle = {
    padding: '10px 16px',
    backgroundColor: isDark ? '#334155' : '#f8fafc',
    border: isDark ? '1px solid #475569' : '1px solid #e2e8f0',
    borderRadius: '12px',
    fontSize: '14px',
    fontWeight: '600',
    color: isDark ? '#e2e8f0' : '#1e293b',
    cursor: 'pointer',
    minWidth: '240px',
    outline: 'none',
  };

  const primaryButtonStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 16px',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '12px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'opacity 0.2s',
  };

  const dropdownStyle = {
    position: 'absolute',
    top: '100%',
    right: 0,
    marginTop: '8px',
    width: '200px',
    backgroundColor: isDark ? '#1e293b' : 'white',
    borderRadius: '12px',
    boxShadow: isDark ? '0 10px 40px rgba(0,0,0,0.4)' : '0 10px 40px rgba(0,0,0,0.15)',
    border: isDark ? '1px solid #334155' : '1px solid #e2e8f0',
    zIndex: 50,
    overflow: 'hidden',
  };

  const dropdownItemStyle = (isDestructive = false) => ({
    width: '100%',
    padding: '10px 12px',
    background: 'none',
    border: 'none',
    textAlign: 'left',
    fontSize: '14px',
    color: isDestructive ? '#ef4444' : (isDark ? '#e2e8f0' : '#475569'),
    cursor: 'pointer',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  });

  return (
    <header style={headerStyle}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
        {/* Menu toggle */}
        <button onClick={toggleSidebar} style={buttonStyle}>
          ☰
        </button>

        {/* Project selector */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <select
            value={selectedProject?.id || ''}
            onChange={(e) => onProjectChange(parseInt(e.target.value))}
            style={selectStyle}
          >
            {projects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
          <button
            onClick={onNewProject}
            style={{
              ...buttonStyle,
              width: '40px',
              height: '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '20px',
            }}
            title="Crear nuevo proyecto"
          >
            +
          </button>
          {selectedProject && (
            <button
              onClick={onEditProject}
              style={{
                ...buttonStyle,
                width: '40px',
                height: '40px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '16px',
              }}
              title="Editar proyecto"
            >
              ✏️
            </button>
          )}
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
        {/* New task button */}
        <button onClick={onNewTask} style={primaryButtonStyle}>
          + Nueva Tarea
        </button>

        {/* Theme toggle */}
        <button
          onClick={toggleTheme}
          style={buttonStyle}
          title={isDark ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro'}
        >
          {isDark ? '☀️' : '🌙'}
        </button>

        {/* Notifications */}
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => setShowNotifications(!showNotifications)}
            style={{
              ...buttonStyle,
              position: 'relative',
            }}
          >
            🔔
            {unreadCount > 0 && (
              <span
                style={{
                  position: 'absolute',
                  top: '6px',
                  right: '6px',
                  width: unreadCount > 9 ? '20px' : '16px',
                  height: '16px',
                  backgroundColor: '#ef4444',
                  borderRadius: '8px',
                  fontSize: '10px',
                  fontWeight: '600',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </button>
          <NotificationPanel
            isOpen={showNotifications}
            onClose={() => setShowNotifications(false)}
          />
        </div>

        {/* User avatar with dropdown */}
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => setShowUserMenu(!showUserMenu)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              borderRadius: '12px',
              transition: 'background-color 0.2s',
            }}
          >
            <Avatar initials={userInitials} src={user?.avatar} size="md" />
            <div style={{ textAlign: 'left' }}>
              <p style={{ fontSize: '14px', fontWeight: '600', color: isDark ? '#e2e8f0' : '#1e293b', margin: 0 }}>
                {user?.name || 'Usuario'}
              </p>
              <p style={{ fontSize: '12px', color: isDark ? '#94a3b8' : '#64748b', margin: 0 }}>
                {user?.dynamicRole?.displayName || user?.role || 'Usuario'}
              </p>
            </div>
            <span style={{ color: '#94a3b8', fontSize: '12px' }}>▼</span>
          </button>

          {/* User dropdown */}
          {showUserMenu && (
            <>
              <div
                onClick={() => setShowUserMenu(false)}
                style={{ position: 'fixed', inset: 0, zIndex: 40 }}
              />
              <div style={dropdownStyle}>
                <div style={{ padding: '12px 16px', borderBottom: isDark ? '1px solid #334155' : '1px solid #f1f5f9' }}>
                  <p style={{ fontSize: '14px', fontWeight: '600', color: isDark ? '#e2e8f0' : '#1e293b', margin: 0 }}>
                    {user?.name}
                  </p>
                  <p style={{ fontSize: '12px', color: isDark ? '#94a3b8' : '#64748b', margin: '2px 0 0 0' }}>
                    {user?.email}
                  </p>
                </div>
                <div style={{ padding: '8px' }}>
                  <button
                    onClick={() => {
                      setShowUserMenu(false);
                      setActiveView('settings');
                    }}
                    style={dropdownItemStyle()}
                  >
                    ⚙️ Configuración
                  </button>
                  <button
                    onClick={() => {
                      setShowUserMenu(false);
                      logout();
                    }}
                    style={dropdownItemStyle(true)}
                  >
                    🚪 Cerrar sesión
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </header>
  );
}

Header.propTypes = {
  projects: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
    })
  ),
  selectedProject: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
  }),
  onProjectChange: PropTypes.func.isRequired,
  onNewTask: PropTypes.func.isRequired,
  onNewProject: PropTypes.func.isRequired,
  onEditProject: PropTypes.func,
};
